import React, { useEffect, useRef, useState } from 'react';
import { Check, SlidersHorizontal } from 'lucide-react';
import { styles, sorts, hero, tours } from '../../egyptListingData';
import { ChevronDown } from './EgyptIcons';
import { NavIcon } from './EgyptNavIcons';
import { scrollToId } from './EgyptHero';
import { useLeadModal } from './LeadModalProvider';

const menus = (style, sort, sortItems) => [
  { key: 'styles', label: tours.filterBy, pick: 'style', items: styles.map((s) => ({ id: s.key, label: s.key, icon: s.icon, selected: style === s.key })) },
  { key: 'sort', label: tours.sortBy, pick: 'sort', items: sortItems.map((s) => ({ id: s.key, label: s.label, icon: s.icon, selected: sort === s.key })) }
];

export const priceSorts = sorts.filter((s) => s.key.startsWith('price-'));
export const priceSortFns = { 'price-asc': (a, b) => a.price - b.price, 'price-desc': (a, b) => b.price - a.price };

export function FilterPills({ style, sort, onStyle, onSort, up = false, testPrefix = 'eg-filter', menuKeys = ['styles', 'sort'], sortItems = sorts, alignRight = false }) {
  const [open, setOpen] = useState(null);
  const [left, setLeft] = useState(0);
  const ref = useRef(null);
  const list = menus(style, sort, sortItems).filter((m) => menuKeys.includes(m.key));

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(null); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('touchstart', onDoc); document.removeEventListener('keydown', onKey); };
  }, []);

  const toggle = (key, e) => {
    if (open === key) return setOpen(null);
    const r = e.currentTarget.getBoundingClientRect();
    const w = ref.current.getBoundingClientRect();
    setLeft(Math.max(0, Math.min(r.left - w.left, w.width - 280)));
    setOpen(key);
  };
  const pick = (menu, item) => {
    const v = item.selected ? null : item.id;
    if (menu.pick === 'style') onStyle(v); else onSort(v);
    setOpen(null);
    if (up) scrollToId('tours');
  };
  const openMenu = list.find((m) => m.key === open);

  return (
    <div ref={ref} className="relative flex items-center gap-2 sm:gap-3">
      {list.map((m) => {
        const active = m.items.some((i) => i.selected);
        const isOpen = open === m.key;
        return (
          <button key={m.key} type="button" onClick={(e) => toggle(m.key, e)} aria-expanded={isOpen} aria-haspopup="menu"
            className={`shrink-0 inline-flex items-center gap-1 h-10 pl-4 pr-3 rounded-full border whitespace-nowrap eg-label-lg transition-colors ${active || isOpen ? 'border-transparent eg-grad-harbor text-white shadow-[0_2px_8px_rgba(23,67,88,0.3)]' : 'border-[#6F777C] text-[#002131] bg-[#FBF9F1] hover:bg-[rgba(23,67,88,0.08)]'}`}
            data-testid={`${testPrefix}-${m.key}`}>
            {m.label}
            {active && <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-white" data-testid={`${testPrefix}-${m.key}-dot`} />}
            <ChevronDown size={20} className={`transition-transform ${isOpen !== up ? 'rotate-180' : ''}`} />
          </button>
        );
      })}
      {openMenu && (
        <div role="menu" style={alignRight ? { right: 0 } : { left }} className={`absolute ${up ? 'bottom-full mb-2' : 'top-full mt-2'} z-30 w-[280px] max-h-[364px] overflow-y-auto no-scrollbar rounded-lg bg-[#F0EEE6] shadow-[0_1px_2px_rgba(0,0,0,0.3),0_2px_6px_2px_rgba(0,0,0,0.15)] animate-[hi-fade-in_150ms_ease-out]`} data-testid={`${testPrefix}-dropdown-${openMenu.key}`}>
          {openMenu.items.map((it) => (
            <button key={it.id} type="button" role="menuitemradio" aria-checked={!!it.selected} onClick={() => pick(openMenu, it)} className="w-full flex items-center gap-3 px-4 py-3 text-left eg-body-lg text-[#002131] hover:bg-[rgba(23,67,88,0.08)] transition-colors" data-testid={`${testPrefix}-item`}>
              <NavIcon name={it.icon} />
              <span className={`flex-1 ${it.selected ? 'font-semibold' : ''}`}>{it.label}</span>
              {it.selected && <Check size={20} className="text-[#174358] shrink-0" aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const SortPill = ({ sort, onSort, testPrefix = 'eg-sort' }) => (
  <FilterPills sort={sort} onSort={onSort} onStyle={() => {}} menuKeys={['sort']} sortItems={priceSorts} testPrefix={testPrefix} alignRight />
);

const useToursInView = () => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const s = document.getElementById('tours');
      if (!s) return;
      const r = s.getBoundingClientRect();
      setInView(r.top < 0 && r.bottom > 160);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return inView;
};

export const FiltersButton = ({ onClick, active = 0, testId = 'eg-filters-button', className = '' }) => (
  <button type="button" onClick={onClick} className={`shrink-0 inline-flex items-center gap-2 h-10 pl-4 pr-4 rounded-full border whitespace-nowrap eg-label-lg transition-colors ${active ? 'border-transparent eg-grad-harbor text-white shadow-[0_2px_8px_rgba(23,67,88,0.3)]' : 'border-[#6F777C] text-[#002131] bg-[#FBF9F1] hover:bg-[rgba(23,67,88,0.08)]'} ${className}`} data-testid={testId}>
    <SlidersHorizontal size={18} />
    Filters
    {active > 0 && <span className="min-w-5 h-5 px-1.5 rounded-full bg-white text-[#174358] text-xs font-bold flex items-center justify-center" data-testid={`${testId}-count`}>{active}</span>}
  </button>
);

export default function EgyptFilterBar({ title, cta = hero, extra = null, ...pills }) {
  const { openLead } = useLeadModal();
  const [stuck, setStuck] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onScroll = () => { if (ref.current) setStuck(ref.current.getBoundingClientRect().top <= 1); };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div ref={ref} className={`hidden md:block sticky top-0 z-30 mt-6 bg-[#FBF9F1] transition-shadow ${stuck ? 'shadow-[0_6px_12px_-8px_rgba(0,33,49,0.25)]' : ''}`} data-testid="eg-filter-bar">
      <div className="flex items-center gap-3 h-16">
        {stuck && <span className="eg-title-md text-[#002131] whitespace-nowrap truncate min-w-0 flex-1 animate-[hi-fade-in_150ms_ease-out]" data-testid="eg-filter-title">{title}</span>}
        {extra}
        <FilterPills {...pills} />
        <a href={cta.ctaHref} onClick={(e) => { e.preventDefault(); openLead(); }} className="eg-btn-filled ml-auto h-11 px-6 eg-title-md" data-testid="eg-filter-cta">{cta.stickyCta}</a>
      </div>
    </div>
  );
}

export function MobileToursBars({ title, filters = true, cta = hero, onOpenFilters, activeCount = 0, ...pills }) {
  const { openLead } = useLeadModal();
  const inTours = useToursInView();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <>
      {filters && (
        <div className={`md:hidden fixed inset-x-0 top-0 z-30 px-4 h-14 flex items-center bg-[#FBF9F1]/95 backdrop-blur shadow-[0_6px_12px_-8px_rgba(0,33,49,0.25)] transition-transform duration-300 ${inTours ? 'translate-y-0' : '-translate-y-full'}`} data-testid="eg-mobile-tours-title" aria-hidden={!inTours}>
          <span className="eg-title-lg text-[#002131] truncate">{title}</span>
        </div>
      )}
      <div className={`md:hidden fixed inset-x-0 bottom-0 z-40 px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] bg-[#FBF9F1]/95 backdrop-blur border-t border-[#E4E3DB] flex flex-col gap-3 transition-transform duration-300 ${show ? 'translate-y-0' : 'translate-y-full'}`} data-testid="eg-mobile-cta" aria-hidden={!show}>
        {filters && (
          <div className="flex items-center gap-2">
            {onOpenFilters && <FiltersButton onClick={onOpenFilters} active={activeCount} testId="eg-mobile-filters-button" />}
            <FilterPills {...pills} up testPrefix="eg-mobile-filter" />
          </div>
        )}
        <a href={cta.ctaHref} onClick={(e) => { e.preventDefault(); openLead(); }} className="eg-btn-filled w-full h-14 eg-title-lg" data-testid="eg-mobile-cta-button">{cta.stickyCta}</a>
      </div>
    </>
  );
}
